// New Relic's browser agent reports every uncaught error and every unhandled
// promise rejection on the page, and it has no browser-side equivalent of the
// `ignore_classes` list config/newrelic.yml uses to keep handled client errors
// out of the Ruby inbox. Without a filter here the browser half of the inbox is
// mostly noise: transitions the browser skipped on purpose, requests the user
// walked away from, and exceptions thrown by scripts injected into the page.
//
// `setErrorHandler` runs one callback per captured error; returning true drops
// it, false keeps it. Only the messages listed below are dropped — anything
// unrecognised is kept, because a filter that grows by guesswork stops being a
// filter and becomes a blindfold.
//
// A string entry matches as a substring, because the agent prefixes rejections
// with "Unhandled Promise Rejection: " while the same condition can also arrive
// unwrapped. A regular expression is for the cases where a substring would be
// too greedy.
const IGNORED_MESSAGES = [
	// Turbo drives document.startViewTransition whenever the
	// <meta name="view-transition"> tag in the layout is present, and its
	// ViewTransitioner awaits `.finished` with nothing catching it. That promise
	// rejects every time the browser skips the transition — a second navigation
	// starting, or the tab being hidden mid-visit — so an interrupted navigation
	// arrives as an unhandled rejection. Three spellings, one per engine.
	"Transition was aborted because of invalid state",
	"Skipped ViewTransition due to document being hidden",
	"View transition was skipped because document visibility state is hidden",

	// A request nobody is waiting for any more: the user navigated away, or
	// Turbo cancelled an in-flight visit. A server failure that actually matters
	// surfaces in the Ruby inbox with a stack trace attached; these carry
	// nothing to act on.
	"The user aborted a request",
	"The operation was aborted",
	"Fetch is aborted",
	// Chrome's message for an AbortController aborted with no reason given.
	"signal is aborted without reason",
	"NetworkError when attempting to fetch resource",
	// Chrome's and Safari's generic fetch failures, anchored to the end of the
	// message so they do not also swallow "Failed to fetch dynamically imported
	// module" — a stale chunk after a deploy is worth seeing if it ever arrives in
	// bulk, and it is the one member of this family that says something.
	/Failed to fetch$/,
	/Load failed$/,

	// Thrown inside a cross-origin script the page did not ask for. The browser
	// strips the message, file and line before the agent sees it, so there is
	// nothing left here to fix.
	"Script error.",

	// Injected by extensions and in-app browsers rather than by this codebase:
	// neither `M_ID` nor `_AutofillCallbackHandler` appears anywhere in
	// app/frontend.
	"reading 'M_ID'",
	"_AutofillCallbackHandler",
	// Microsoft's link scanner (Outlook Safe Links) rendering a mailed URL.
	"Object Not Found Matching Id:",

	// The spec's notice when a ResizeObserver callback resizes what it observes
	// (our islands set state from theirs); the rest arrives the next frame.
	"ResizeObserver loop completed with undelivered notifications",
];

function matches(message, pattern) {
	return typeof pattern === "string" ? message.includes(pattern) : pattern.test(message);
}

export function shouldIgnoreError(error) {
	const message = typeof error === "string" ? error : error?.message;
	if (typeof message !== "string") return false;

	return IGNORED_MESSAGES.some((pattern) => matches(message, pattern));
}

// The agent's loader is a deferred script placed before the app bundle, so it is
// already on globalThis by the time this module — deferred, like every ES module —
// runs. It is absent when a tracker blocker drops the loader, and in
// development, where config/newrelic.yml sets monitor_mode: false and
// browser_timing_header emits nothing, so a missing agent is normal rather than
// a failure worth reporting.
export function installBrowserErrorFilter(agent = globalThis.newrelic) {
	if (typeof agent?.setErrorHandler !== "function") return false;

	agent.setErrorHandler(shouldIgnoreError);
	return true;
}

installBrowserErrorFilter();
